// Trigger sets — authored per report; see docs/builder-report-authoring-spec.md.
import type { TriggerQuestion } from "./types";
import {
  WAH,
  SILICA,
  HAVS,
  MANUAL_HANDLING,
  SLIPS_TRIPS,
  PLANT_PEDESTRIAN,
  FALLING_MATERIAL_PUBLIC,
  ACCESS_OPTIONS,
  COSHH_DETAIL_QS,
  RESCUE_Q,
} from "./shared";

// Report-local hazard ids (minted in this batch's JSONs).
const CEMENT_DERMATITIS = "haz-coshh-cement-dermatitis-and-chemical-burn";
const LIME_MORTAR = "haz-coshh-lime-mortar-caustic-burn";
const WALL_COLLAPSE = "haz-structural-stability-newly-built-or-unset-wall-collapse";
const REBAR_IMPALEMENT = "haz-steel-fixing-impalement-on-projecting-rebar";
const REBAR_CUTS = "haz-steel-fixing-cut-and-laceration-from-bar-and-tie-wire";
const REBAR_EYE = "haz-steel-fixing-eye-injury-from-springing-tie-wire";
const OFFLOAD_STRUCK = "haz-lifting-struck-by-suspended-or-shed-load-during-offloading";
const FALSEWORK_COLLAPSE = "haz-temporary-works-falsework-or-formwork-collapse";
const NAIL_GUN = "haz-tools-nail-gun-injury";
const RELEASE_AGENT = "haz-coshh-release-agent-exposure";
const STRIKE_STRUCK = "haz-temporary-works-struck-by-panel-or-prop-during-striking";
const CEMENT_BURN = "haz-coshh-cement-burn-from-skin-contact";
const PUMP_LINE = "haz-concreting-concrete-pump-line-blockage-or-blow-out";
const WASHOUT = "haz-environmental-concrete-washout-alkaline-runoff";

export const TRADES_STRUCTURAL_TRIGGERS: Record<string, TriggerQuestion[]> = {
  "bricklaying-blockwork": [
    {
      key: "atHeight",
      label: "Will any laying be done off a scaffold or other work at height?",
      kind: "yesno",
      effects: {
        yes: { hazardsOn: [WAH], scopeLine: "Laying off a scaffold or platform — work-at-height controls apply; lay only off a tagged, handed-over platform and never above shoulder height." },
        no: { hazardsOff: [WAH], emergencyOff: ["Fall from the scaffold"], scopeLine: "All laying is at or near ground level — no scaffold or platform work in this package, so work-at-height controls are not required." },
      },
      notSure: { hazardsOn: [WAH], failCheck: "Work-at-height requirement confirmed" },
      followUps: {
        when: "yes",
        questions: [
          {
            key: "accessMethod",
            label: "What access equipment will be used?",
            kind: "choice",
            options: ACCESS_OPTIONS,
            effects: Object.fromEntries(ACCESS_OPTIONS.map((o) => [o, { scopeLine: `Access for work at height: ${o}.` }])),
          },
          RESCUE_Q,
        ],
      },
    },
    {
      key: "publicBelow",
      label: "Are occupants, the public or other trades below or beside the working lift?",
      kind: "yesno",
      effects: {
        yes: { hazardsOn: [FALLING_MATERIAL_PUBLIC], requireFields: ["nearbyTradesOrPublic"], scopeLine: "People are below or beside the work — brick guards, toe-boards, netting and exclusion zones below loading bays are maintained." },
        no: { hazardsOff: [FALLING_MATERIAL_PUBLIC], scopeLine: "No occupants, public or other trades below or beside the working lift during the works." },
      },
      notSure: { hazardsOn: [FALLING_MATERIAL_PUBLIC], failCheck: "People below / beside the work confirmed" },
    },
    {
      key: "cutting",
      label: "Will bricks or blocks be cut or ground on site (dust)?",
      kind: "yesno",
      effects: {
        yes: { hazardsOn: [SILICA] },
        no: { hazardsOff: [SILICA], emergencyOff: ["Silica dust"], substancesOff: ["Respirable crystalline silica"], scopeLine: "All units are ordered pre-cut or split with a hydraulic splitter — no on-site sawing, so silica-dust controls are not required for this task." },
      },
      notSure: { hazardsOn: [SILICA], failCheck: "On-site cutting / grinding requirement confirmed" },
      followUps: {
        when: "yes",
        questions: [
          {
            key: "cutMethod",
            label: "How will units be cut?",
            kind: "choice",
            options: ["Bench/cut-off saw with water suppression", "Hydraulic block splitter (no dust)", "Dry-cutting (not permitted)"],
            effects: {
              "Bench/cut-off saw with water suppression": { scopeLine: "Cutting on a bench/cut-off saw with integral water suppression; FFP3 RPE worn." },
              "Hydraulic block splitter (no dust)": { scopeLine: "Cutting by hydraulic block splitter — no dust generated." },
              "Dry-cutting (not permitted)": { failCheck: "Dry-cutting is not acceptable — provide water suppression or a block splitter" },
            },
          },
          ...COSHH_DETAIL_QS,
        ],
      },
    },
    {
      key: "lime",
      label: "Is lime mortar used (heritage or conservation work)?",
      kind: "yesno",
      effects: {
        yes: { hazardsOn: [LIME_MORTAR], scopeLine: "Lime mortar in use — caustic-burn controls apply; pre-slaked lime putty preferred to on-site slaking." },
        no: { hazardsOff: [LIME_MORTAR], scopeLine: "No lime mortar — only cement-based mortar is used, so hot-lime/caustic-lime controls are not required for this task." },
      },
    },
    {
      key: "freeStanding",
      label: "Will any free-standing, gable or tall single-skin walls be built that need temporary restraint before they set?",
      kind: "yesno",
      effects: {
        yes: { hazardsOn: [WALL_COLLAPSE], scopeLine: "Free-standing or tall unset walls — propping/restraint and a safe unsupported-height limit apply; stop and brace in high wind." },
        no: { hazardsOff: [WALL_COLLAPSE], scopeLine: "Walls are built between permanent restraints (floors, returns, ties) with no free-standing unset sections, so temporary wall-restraint controls are not required for this task." },
      },
      notSure: { hazardsOn: [WALL_COLLAPSE], failCheck: "Temporary wall-stability requirement confirmed" },
    },
    {
      key: "washing",
      label: "Are forearm-washing facilities (hot water, large basins, barrier cream) available before work starts?",
      kind: "yesno",
      effects: {
        yes: { scopeLine: "Forearm-washing facilities (hot water, large basins, barrier and after-work cream) are available before work starts." },
        no: { failCheck: "Cement skin-contact washing facilities confirmed" },
      },
      notSure: { failCheck: "Cement skin-contact washing facilities confirmed" },
    },
  ],

  "steel-fixing": [
    {
      key: "projectingBars",
      label: "Will any bars project where a person could fall, trip or be impaled?",
      kind: "yesno",
      effects: {
        yes: { hazardsOn: [REBAR_IMPALEMENT], scopeLine: "Projecting/starter bars present — every bar is capped with fall-rated troughs or caps where a fall onto bars is foreseeable, kept on until the bars are encased." },
        no: { hazardsOff: [REBAR_IMPALEMENT], emergencyOff: ["Impalement"], scopeLine: "No projecting or starter bars left exposed — bars are designed out or bent over, so impalement controls are not required for this task." },
      },
      notSure: { hazardsOn: [REBAR_IMPALEMENT], failCheck: "Projecting / starter bar status confirmed" },
      followUps: {
        when: "yes",
        questions: [
          {
            key: "capStandard",
            label: "What rebar-end protection is used?",
            kind: "choice",
            options: ["Fall-rated troughs / caps (fall onto bars possible)", "Plastic mushroom caps (scratch/cut only, no fall risk)"],
            effects: {
              "Fall-rated troughs / caps (fall onto bars possible)": { scopeLine: "Rebar-end protection: impact-resistant troughs/caps rated for fall protection." },
              "Plastic mushroom caps (scratch/cut only, no fall risk)": { scopeLine: "Rebar-end protection: plastic mushroom caps — used only where no fall onto bars is foreseeable." },
            },
          },
        ],
      },
    },
    {
      key: "offloading",
      label: "Will rebar be offloaded or distributed by crane or telehandler?",
      kind: "yesno",
      effects: {
        yes: { hazardsOn: [OFFLOAD_STRUCK], permitsAdd: ["Lift plan / permit for crane or telehandler offloading"], scopeLine: "Mechanical offloading — to a competent appointed-person lift plan, trained slinger and exclusion zone under the load." },
        no: { hazardsOff: [OFFLOAD_STRUCK], permitsRemove: ["Lift plan / permit for crane or telehandler offloading"], emergencyOff: ["Crushing or struck-by during offloading"], scopeLine: "No crane or telehandler offloading — bar is delivered and handled manually within safe limits, so a lifting plan is not required for this task." },
      },
      notSure: { hazardsOn: [OFFLOAD_STRUCK], permitsAdd: ["Lift plan / permit for crane or telehandler offloading"], failCheck: "Mechanical offloading requirement confirmed" },
      followUps: {
        when: "yes",
        questions: [
          {
            key: "slinger",
            label: "Appointed person / slinger for the lift (name / role)",
            kind: "text",
            placeholder: "e.g. lift plan by A Jones (AP); slinger M Patel",
            requiredCheck: "Lift plan author and slinger named",
            effects: { "*": { scopeLine: "Offloading lift plan and slinger: {answer}." } },
          },
        ],
      },
    },
    {
      key: "matWalkways",
      label: "Will boarded walkways / crawl boards be provided across completed mats?",
      kind: "yesno",
      effects: {
        yes: { scopeLine: "Boarded walkways and crawl boards are laid across completed mats so operatives are not walking directly on tied steel." },
        no: { hazardsOn: [SLIPS_TRIPS], failCheck: "Walkways / crawl boards across the mat confirmed" },
      },
      notSure: { hazardsOn: [SLIPS_TRIPS], failCheck: "Walkways / crawl boards across the mat confirmed" },
    },
    {
      key: "poweredTie",
      label: "Will a powered tying tool be used (rather than hand-tying with the wire near the face)?",
      kind: "yesno",
      effects: {
        yes: { scopeLine: "Reinforcement is tied with a powered tying tool, keeping hands and faces away from springing wire ends." },
        no: { hazardsOn: [REBAR_CUTS, REBAR_EYE], scopeLine: "Hand-tying — cut-resistant gloves and impact-rated eye protection are mandatory and wire is directed away from the face." },
      },
    },
  ],

  "formwork-shuttering": [
    {
      key: "twDesign",
      label: "Is a checked temporary works design and a named temporary works coordinator (TWC) in place?",
      kind: "yesno",
      effects: {
        yes: { scopeLine: "A checked temporary works design (BS 5975) and a named TWC are in place; formwork is erected, loaded and struck strictly to that design." },
        no: { failCheck: "Temporary works design and TWC sign-off confirmed (formwork must not proceed without them)" },
      },
      notSure: { failCheck: "Temporary works design and TWC sign-off confirmed (formwork must not proceed without them)" },
      followUps: {
        when: "yes",
        questions: [
          {
            key: "twcName",
            label: "Temporary works coordinator and design reference",
            kind: "text",
            placeholder: "e.g. TWC J Owen; TW design TW-12 rev B, cat 2 check",
            requiredCheck: "TWC and TW design reference recorded",
            effects: { "*": { scopeLine: "TWC and temporary works design: {answer}." } },
          },
        ],
      },
    },
    {
      key: "strikeAuth",
      label: "Will striking be authorised in writing by the TWC against a strength criterion (not just elapsed time)?",
      kind: "yesno",
      effects: {
        yes: { scopeLine: "Striking is authorised by the TWC in writing (permit to strike) once the specified concrete strength is confirmed, following the designed sequence." },
        no: { hazardsOn: [FALSEWORK_COLLAPSE, STRIKE_STRUCK], failCheck: "Permit-to-strike and strength criterion confirmed" },
      },
      notSure: { hazardsOn: [FALSEWORK_COLLAPSE, STRIKE_STRUCK], failCheck: "Permit-to-strike and strength criterion confirmed" },
    },
    {
      key: "atHeight",
      label: "Will formwork be erected or struck at height (slab edges, tall walls, soffits)?",
      kind: "yesno",
      effects: {
        yes: { hazardsOn: [WAH], scopeLine: "Erection/striking at height — integral access platforms with guard rails or a tied, inspected scaffold are used and edge protection maintained." },
        no: { hazardsOff: [WAH], emergencyOff: ["Fall from height"], scopeLine: "All formwork is erected and struck at or near ground level — no work at height, so work-at-height controls are not required for this task." },
      },
      notSure: { hazardsOn: [WAH], failCheck: "Work-at-height requirement confirmed" },
      followUps: { when: "yes", questions: [RESCUE_Q] },
    },
    {
      key: "nailGun",
      label: "Will nail guns / cartridge tools be used to assemble or strip panels?",
      kind: "yesno",
      effects: {
        yes: { hazardsOn: [NAIL_GUN], scopeLine: "Nail guns in use — sequential-trip tools only, PUWER-trained operators, free hand clear of the firing line." },
        no: { hazardsOff: [NAIL_GUN], emergencyOff: ["Nail-gun injury"], scopeLine: "No nail guns — panels are assembled with hand tools or proprietary system clamps, so nail-gun controls are not required for this task." },
      },
    },
    {
      key: "releaseAgent",
      label: "Will release agent be applied (especially sprayed in enclosed forms)?",
      kind: "yesno",
      effects: {
        yes: { hazardsOn: [RELEASE_AGENT] },
        no: { hazardsOff: [RELEASE_AGENT], substancesOff: ["Mould-release agent"], scopeLine: "No release agent used (pre-treated panels or none required), so release-agent COSHH controls are not required for this task." },
      },
      notSure: { hazardsOn: [RELEASE_AGENT], failCheck: "Release-agent use confirmed" },
      followUps: { when: "yes", questions: COSHH_DETAIL_QS },
    },
    {
      key: "panelLifting",
      label: "Will large panels be moved or lowered by crane or mechanical aid?",
      kind: "yesno",
      effects: {
        yes: { scopeLine: "Large panels are moved and lowered with mechanical assistance or a tag-controlled lift, with an exclusion zone below." },
        no: { hazardsOn: [MANUAL_HANDLING], scopeLine: "Panels are handled manually — two-person lifts within safe limits and a manual-handling assessment apply." },
      },
    },
  ],

  "concrete-pour": [
    {
      key: "pumped",
      label: "Will concrete be placed by pump (rather than skip or direct discharge only)?",
      kind: "yesno",
      effects: {
        yes: { hazardsOn: [PUMP_LINE], scopeLine: "Concrete is placed by pump — couplings secured, the placing hose restrained against whip, and a stop-reverse-depressurise procedure for any blockage." },
        no: { hazardsOff: [PUMP_LINE], emergencyOff: ["Pump-line blow-out", "Caught in a pump-line whip"], scopeLine: "No concrete pump — placing is by crane skip or direct chute discharge, so pump-line blow-out controls are not required for this task." },
      },
      notSure: { hazardsOn: [PUMP_LINE], failCheck: "Placing method (pump vs skip) confirmed" },
    },
    {
      key: "vehicleInterface",
      label: "Will ready-mix trucks, a pump or other plant set up near pedestrians?",
      kind: "yesno",
      effects: {
        yes: { hazardsOn: [PLANT_PEDESTRIAN], requireFields: ["nearbyTradesOrPublic"], scopeLine: "Truck/pump set-up near people — segregated, banked vehicle movements to the PC traffic plan, with exclusion under the pump boom." },
        no: { hazardsOff: [PLANT_PEDESTRIAN], emergencyOff: ["Vehicle or plant collision"], scopeLine: "Trucks and plant set up in an isolated area with no pedestrian interface, so vehicle/pedestrian segregation controls beyond the standard site plan are not required for this task." },
      },
      notSure: { hazardsOn: [PLANT_PEDESTRIAN], failCheck: "Vehicle/pedestrian interface confirmed" },
      followUps: {
        when: "yes",
        questions: [
          {
            key: "banksman",
            label: "Banksman / traffic-management arrangement (name / plan ref)",
            kind: "text",
            placeholder: "e.g. banksman R Cole; PC traffic plan TM-3",
            requiredCheck: "Banksman / traffic plan recorded",
            effects: { "*": { scopeLine: "Vehicle banking / traffic management: {answer}." } },
          },
        ],
      },
    },
    {
      key: "vibration",
      label: "Will poker / immersion vibrators be used to compact?",
      kind: "yesno",
      effects: {
        yes: { hazardsOn: [HAVS], scopeLine: "Poker vibrators in use — low-vibration tools, trigger-time limits and operative rotation under the Vibration Regulations." },
        no: { hazardsOff: [HAVS], scopeLine: "No hand-held pokers — compaction is by screed-mounted or machine-mounted vibration, so hand-arm vibration controls are not required for this task." },
      },
      notSure: { hazardsOn: [HAVS], failCheck: "Compaction method (hand poker vs machine-mounted) confirmed" },
    },
    {
      key: "washout",
      label: "Will concrete washout / wash water be generated on site?",
      kind: "yesno",
      effects: {
        yes: { hazardsOn: [WASHOUT], scopeLine: "Washout is captured in a designated bunded/lined area away from drains and watercourses, settled or neutralised before controlled disposal." },
        no: { hazardsOff: [WASHOUT], scopeLine: "No on-site washout — trucks and pump wash off site, so on-site washout-containment controls are not required for this task." },
      },
    },
    {
      key: "permitToLoad",
      label: "Is the formwork inspected and the permit to load in place before the pour?",
      kind: "yesno",
      effects: {
        yes: { scopeLine: "The formwork/falsework is inspected and the permit to load is in place; the pour does not start until it is, and the design pour rate is not exceeded." },
        no: { failCheck: "Permit to load on the formwork confirmed before the pour" },
      },
      notSure: { failCheck: "Permit to load on the formwork confirmed before the pour" },
    },
    {
      key: "cementWashing",
      label: "Are forearm-washing facilities and knee protection in place for finishers working in wet concrete?",
      kind: "yesno",
      effects: {
        yes: { scopeLine: "Forearm-washing facilities (hot water, large basins, barrier cream) and waterproof knee protection are provided for finishers." },
        no: { failCheck: "Cement-burn washing facilities and knee protection confirmed" },
      },
      notSure: { failCheck: "Cement-burn washing facilities and knee protection confirmed" },
    },
  ],
};
